import React from 'react';

const MainPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Hero Section */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="text-center max-w-4xl mx-auto">
            <div className="mb-8">
              <h1 className="text-5xl font-bold text-gray-900 mb-4">
                Abraham Romero
              </h1>
              <p className="text-xl text-gray-600 mb-6">
                Software Engineer & Technology Professional
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-max">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              About Me
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              I'm a passionate software engineer with expertise in modern web technologies 
              and a strong foundation in software development principles. I enjoy creating 
              innovative solutions and contributing to meaningful projects that make a difference.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Technical Skills
                </h3>
                <p className="text-gray-600">
                  Full-stack development, React, TypeScript, Node.js, and modern web technologies
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Interests
                </h3>
                <p className="text-gray-600">
                  Game development, AI/ML, open source contributions, and emerging technologies
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
              Let's Connect
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <a 
                href="https://github.com/kalm004" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group bg-gray-50 hover:bg-gray-100 p-6 rounded-xl transition-all duration-300 border border-gray-200 hover:border-gray-300"
              >
                <div className="text-center">
                  <div className="w-12 h-12 bg-gray-800 rounded-lg mx-auto mb-4 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">GitHub</h3>
                  <p className="text-sm text-gray-600">View my projects</p>
                </div>
              </a>

              <a 
                href="https://www.linkedin.com/in/abraham-romero-quesada-11252823/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group bg-gray-50 hover:bg-gray-100 p-6 rounded-xl transition-all duration-300 border border-gray-200 hover:border-gray-300"
              >
                <div className="text-center">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg mx-auto mb-4 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">LinkedIn</h3>
                  <p className="text-sm text-gray-600">Professional profile</p>
                </div>
              </a>

              <a 
                href="mailto:aromqueu@gmail.com"
                className="group bg-gray-50 hover:bg-gray-100 p-6 rounded-xl transition-all duration-300 border border-gray-200 hover:border-gray-300"
              >
                <div className="text-center">
                  <div className="w-12 h-12 bg-red-500 rounded-lg mx-auto mb-4 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-.904.732-1.636 1.636-1.636h.682L12 13.5l9.682-9.68h.682A1.636 1.636 0 0 1 24 5.457z"/>
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Email</h3>
                  <p className="text-sm text-gray-600">aromqueu@gmail.com</p>
                </div>
              </a>

              <a 
                href="https://kalm004.itch.io/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group bg-gray-50 hover:bg-gray-100 p-6 rounded-xl transition-all duration-300 border border-gray-200 hover:border-gray-300"
              >
                <div className="text-center">
                  <div className="w-12 h-12 bg-orange-500 rounded-lg mx-auto mb-4 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Itch.io</h3>
                  <p className="text-sm text-gray-600">Game projects</p>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="container-max text-center">
          <p className="text-gray-400">
            © 2025 Abraham Romero Quesada. Built with React & TypeScript.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default MainPage;